import React, { useCallback, useEffect, memo, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'lodash-es';
import { createSelector } from 'reselect';
import { useHistory, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import {
  getHotSearchBox,
  getLenovoWords,
  cleanSearchRecommend,
  setIsSearchClick,
} from '../../actions/search';
import { setSearchKeyWord, setHotLenovoVisible } from '../../actions/layout';
import styles from './Search.module.less';
import HotSearch from './HotSearch';
import LenovoWords from './LenovoWords';
import { CONTEXT } from '../../config';
import { logPostInfo, logging } from '../../actions/log';
import { getQueryString } from '../../utils/utils';
import CoverSearch from './CoverSearch';

const selectHotSearchBox = createSelector(
  state => state.search,
  search => ({
    hotSearch: (search.hotSearch && search.hotSearch.slice(0, 8)) || [],
    hotSearchApp: search.hotSearchApp,
    searchBarWordList: search.searchBarWordList,
  }),
);

const selectHotLenovoVisible = createSelector(
  state => state.layout,
  layout => layout.hotLenovoVisible,
);

const selectLenovoWords = createSelector(
  state => state.search,
  search => ({
    isSearchClick: search.isSearchClick,
    lenovoWords: search.lenovoWords,
  }),
);

const selectLogInfo = createSelector(
  state => state.log,
  log => log.logInfo,
);
const selectTopImgVideoVisible = createSelector(
  state => state.layout,
  layout => layout.topImgVideo,
);

const selectSearchResultObj = createSelector(
  state => state.searchResult,
  searchResult => searchResult,
);

let g_search_change_timeindex = 0;
export default memo(() => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const { hotSearch: hotSearchData } = useSelector(selectHotSearchBox);
  const hotLenovoVisible = useSelector(selectHotLenovoVisible);
  const { lenovoWords, isSearchClick } = useSelector(selectLenovoWords);
  const logInfoData = useSelector(selectLogInfo);
  const topImgVideo = useSelector(selectTopImgVideoVisible); // 头部图片视频
  const { searchParam } = useSelector(selectSearchResultObj); // 设置搜索参数，用于在搜索结果页面的时候在某个tab下搜索就指向某个tab

  const [value, setValue] = useState('');
  const [changeIndex, setChangeIndex] = useState(null);

  const [inputFoucus, setInputFocus] = useState(false);
  const [placeholder, setPlaceholder] = useState(null);
  const [inputDom, setInputDom] = useState(null);

  // 当hotLenovoVisible === 0时自动将其focus到输入框
  useEffect(() => {
    if (inputDom && hotLenovoVisible === 0) {
      inputDom.focus();
      setInputFocus(true);
    }
  }, [inputDom, hotLenovoVisible]);

  /**
   *
   * @param {*} v  关键词
   * @param {*} type  关键词类型 新、爆（如果来源与热词才有该值（非输入框））
   * @param {*} input 是否是来源于输入框
   */
  const handleForceSearch = useCallback(
    (v, type, input) => {
      if (!v) {
        return;
      }
      dispatch(
        logging({
          category: 'C',
          action: input ? 'Search_Title' : `Search_Hot`,
          label: '',
          param2: {
            source: logInfoData.source,
            source_p: logInfoData.source_p,
            source_pp: logInfoData.source_pp,
            search_type: input ? 7 : 1, // 搜索框 、热词
            search_word: v,
            type,
          },
        }),
      );
      dispatch(
        logPostInfo({
          source: 'Search',
          source_p: logInfoData.source,
          source_pp: logInfoData.source_p,
          search_word: v,
          search_type: input ? 7 : 1, // 搜索框 、热词
        }),
      );
      setValue(v);
      document.getElementById('container_content').scrollTo(0, 0);

      // 设置搜索参数，用于在搜索结果页面的时候，触发搜索，在某个tab下搜索就指向某个tab
      if (history?.location?.pathname?.indexOf('searchresult') > -1 && searchParam.activeTab) {
        history.push(
          `${CONTEXT}/searchresult?keyword=${encodeURIComponent(v)}&activeTab=${
            searchParam.activeTab
          }`,
        );
      } else {
        history.push(`${CONTEXT}/searchresult?keyword=${encodeURIComponent(v)}`);
      }
    },
    [logInfoData, history, searchParam],
  );

  // 搜索框下 hotLenovoVisible === 0 ? 热词 : 联想词
  const handleSearch = useCallback(() => {
    // search_type 1: 热词    2: 联想词   3: 搜索词
    dispatch(
      logPostInfo({
        source: 'Search',
        source_p: logInfoData.source,
        source_pp: logInfoData.source_p,
        search_word: value,
        search_type: 3,
      }),
    );
    dispatch(setHotLenovoVisible(-1));
    const keyword = getQueryString('keyword');
    if (!value.trim() || keyword === value) {
      return;
    }
    dispatch(cleanSearchRecommend());
    // 新搜索时页面滚动到顶部
    if (
      document.getElementById('container_content') &&
      document.getElementById('container_content').scrollTo
    ) {
      document.getElementById('container_content').scrollTo(0, 0);
    }
    // 设置搜索参数，用于在搜索结果页面的时候，触发搜索，在某个tab下搜索就指向某个tab
    if (history?.location?.pathname?.indexOf('searchresult') > -1 && searchParam.activeTab) {
      history.push(
        `${CONTEXT}/searchresult?keyword=${encodeURIComponent(value)}&activeTab=${
          searchParam.activeTab
        }`,
      );
    } else {
      history.push(`${CONTEXT}/searchresult?keyword=${encodeURIComponent(value)}`);
    }
  }, [logInfoData, history, searchParam, value]);

  const handleChange = useCallback(
    ev => {
      ev.persist();
      const inputValue = ev.target.value;
      setValue(inputValue);
      clearTimeout(g_search_change_timeindex);
      if (!inputValue.trim()) {
        g_search_change_timeindex = setTimeout(() => {
          dispatch(setHotLenovoVisible(0));
          dispatch(getLenovoWords(inputValue));
        }, 500);
      } else {
        dispatch(setHotLenovoVisible(1));
        debounce(() => {
          setChangeIndex(null);
          dispatch(setSearchKeyWord(inputValue));
          dispatch(getLenovoWords(inputValue));
        }, 50)();
      }
    },
    [dispatch],
  );

  const handleArrowDownUp = (key, _changeIndex, _lenovoWords, _hotLenovoVisible) => {
    if (_lenovoWords.length > 0 && _hotLenovoVisible === 1) {
      if (key === 'ArrowDown') {
        if (_changeIndex === null) {
          setChangeIndex(0);
          setValue(_lenovoWords[0].keyWord);
        } else if (_changeIndex < _lenovoWords.length - 1) {
          setChangeIndex(_changeIndex + 1);
          setValue(_lenovoWords[_changeIndex + 1].keyWord);
        }
      }
      if (key === 'ArrowUp') {
        if (_changeIndex !== null && _changeIndex > 0) {
          setChangeIndex(_changeIndex - 1);
          setValue(_lenovoWords[_changeIndex - 1].keyWord);
        }
      }
    }
  };

  const handleKeyDown = useCallback(
    ev => {
      ev.persist();
      ev.stopPropagation();
      if (ev.key === 'Enter' && value) {
        handleSearch();
      }
      if ((ev.key === 'ArrowDown' || ev.key === 'ArrowUp') && value) {
        if (lenovoWords && lenovoWords.data && lenovoWords.data.length > 0) {
          handleArrowDownUp(ev.key, changeIndex, lenovoWords.data.slice(0, 6), hotLenovoVisible);
        }
      }
    },
    [value, changeIndex, lenovoWords, hotLenovoVisible],
  );

  const handleFocus = useCallback(
    ev => {
      if (!ev.target.value.trim()) {
        dispatch(setHotLenovoVisible(0));
      }
      setInputFocus(true);
    },
    [dispatch],
  );

  useEffect(() => {
    if (hotSearchData && hotSearchData.length === 0) {
      dispatch(getHotSearchBox());
    }
  }, [dispatch]);

  useEffect(() => {
    setValue(getQueryString('keyword') || '');
  }, [location.search]);

  const handleClearKeyword = useCallback(() => {
    setValue('');
  }, [value]);

  useEffect(() => {
    if (!isSearchClick && hotLenovoVisible !== -1) {
      dispatch(setHotLenovoVisible(-1));
    }
  }, [isSearchClick]);

  return (
    <div
      className={
        topImgVideo && topImgVideo.visible && !topImgVideo.isScrolled
          ? styles.search_white
          : styles.search
      }
      onClickCapture={() => {
        dispatch(setIsSearchClick(true));
      }}
    >
      <input
        type="text"
        className="input"
        ref={dom => {
          setInputDom(dom);
        }}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        value={value}
        onFocus={handleFocus}
        onBlur={() => {
          setInputFocus(false);
        }}
      />
      <i
        className={`icon iconfont icon-opt_search ${inputFoucus ? 'icon_opt_search_gray' : ''}`}
        onMouseDownCapture={e => {
          if (!inputFoucus && !value) {
            // 新搜索时页面滚动到顶部
            handleForceSearch(placeholder, null, true);
          } else {
            handleSearch(e);
          }
        }}
      />
      <CoverSearch
        isWhite={topImgVideo && topImgVideo.visible && !topImgVideo.isScrolled}
        isCoverSearch={!value}
        getPlaceHolder={v => {
          setPlaceholder(v);
        }}
      />
      {false && value && (
        <i
          className={classNames('icon iconfont icon-search_clear', styles.clear_keyword)}
          onClick={handleClearKeyword}
        />
      )}
      {hotLenovoVisible === 0 && (
        <HotSearch
          handelValue={(v, type) => {
            setValue(v);
            // 新搜索时页面滚动到顶部
            handleForceSearch(v, type);
          }}
        />
      )}
      {hotLenovoVisible === 1 && <LenovoWords changeIndex={changeIndex} value={value} />}
    </div>
  );
});
